import React, { useState, useEffect } from "react";
import { Form, Button } from "react-bootstrap";
import { useTranslate } from "../hooks/LanguageProvider";
import { Bounce, toast } from "react-toastify";
import { useParams } from "react-router-dom";
import Select from "react-select";

const backendUrl = process.env.REACT_APP_BACKEND_URL;

interface UserInterface {
  id_user: number;
  nom_user: string;
  prenom_user: string;
}

interface SelectOption {
  value: number | string;
  label: string;
}

const NotificationCenter: React.FC = () => {
  const { translate } = useTranslate();
  const { id_user } = useParams<{ id_user?: string }>();
  const user_id = localStorage.getItem("userid");

  const [usersOptions, setUsersOptions] = useState<SelectOption[]>([]);
  const [selectedUser, setSelectedUser] = useState<SelectOption | null>(null);
  const [title, setTitle] = useState<string>("");
  const [body, setBody] = useState<string>("");
  const [buttonClicked, setButtonClicked] = useState(false);

  useEffect(() => {
    if (!user_id) return;
    getUsers(user_id);
  }, [user_id]);

  useEffect(() => {
    if (id_user && usersOptions.length > 0) {
      const preselected = usersOptions.find(
        (u) => Number(u.value) === Number(id_user),
      );
      if (preselected) {
        setSelectedUser(preselected);
      }
    }
  }, [id_user, usersOptions]);

  const getUsers = async (currentUserId: string) => {
    try {
      const res = await fetch(`${backendUrl}/api/users/find/${currentUserId}`, {
        mode: "cors",
      });

      if (!res.ok) {
        console.error("Erreur lors de la récupération des utilisateurs");
        return;
      }

      const usersData: UserInterface[] = await res.json();

      const usersOptionsData: SelectOption[] = usersData.map((user) => ({
        value: user.id_user,
        label: `${user.nom_user} ${user.prenom_user || ""}`.trim(),
      }));

      setUsersOptions(usersOptionsData);
    } catch (error) {
      console.error("Erreur lors de la récupération des utilisateurs", error);
    }
  };

  const sendNotification = async () => {
    if (!selectedUser || title.trim() === "" || body.trim() === "") {
      toast.warn(translate("Please fill in all required fields"), {
        position: "bottom-right",
        autoClose: 2400,
        theme: "light",
        transition: Bounce,
      });
      return;
    }

    try {
      setButtonClicked(true);

      const res = await fetch(`${backendUrl}/api/notification/send`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        mode: "cors",
        body: JSON.stringify({
          id_user: selectedUser.value,
          title,
          body,
        }),
      });

      const data = await res.json();

      if (!res.ok || !data.success) {
        toast.error(translate("Error while sending notification"), {
          position: "bottom-right",
          autoClose: 2400,
          theme: "light",
          transition: Bounce,
        });
        return;
      }

      toast.success(translate("Notification sent successfully"), {
        position: "bottom-right",
        autoClose: 2400,
        theme: "light",
        transition: Bounce,
      });

      const details = `Send notification to user ${selectedUser.value}`;
      const timestamp = new Date(Date.now() + 60 * 60 * 1000)
        .toISOString()
        .slice(0, 19)
        .replace("T", " ");

      if (user_id) {
        await fetch(`${backendUrl}/api/log-user-action/${user_id}`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            action: "Send",
            page: "NotificationCenter",
            operation: "Send notification",
            timestamp,
            details,
          }),
        });
      }

      setTitle("");
      setBody("");
      if (!id_user) {
        setSelectedUser(null);
      }
    } catch (error) {
      console.error("Erreur :", error);
      toast.error(translate("Server error"), {
        position: "bottom-right",
        autoClose: 2400,
        theme: "light",
        transition: Bounce,
      });
    } finally {
      setButtonClicked(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
  };

  const fieldStyle = {
    minHeight: "48px",
    borderRadius: "10px",
    border: "1px solid #cbd5e1",
    boxShadow: "none",
    fontSize: "14px",
    color: "#0f172a",
  } as const;

  const textareaStyle = {
    ...fieldStyle,
    minHeight: "140px",
    paddingTop: "12px",
  } as const;

  const labelStyle = {
    color: "#0f172a",
    fontWeight: 600,
    marginBottom: "8px",
    display: "block",
  } as const;

  const primaryButtonBaseStyle = {
    background: "#facc15",
    border: "none",
    color: "#111827",
    borderRadius: "10px",
    padding: "10px 18px",
    fontWeight: 700,
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    boxShadow: "none",
    outline: "none",
    transition: "all 0.15s ease",
  } as const;

  const secondaryButtonBaseStyle = {
    background: "#f1f5f9",
    border: "1px solid #e2e8f0",
    color: "#475569",
    borderRadius: "10px",
    padding: "10px 18px",
    fontWeight: 600,
    boxShadow: "none",
    outline: "none",
    transition: "all 0.15s ease",
  } as const;

  const selectStyles = {
    control: (base: any, state: any) => ({
      ...base,
      minHeight: "48px",
      borderRadius: "10px",
      borderColor: state.isFocused ? "#cbd5e1" : "#cbd5e1",
      boxShadow: "none",
      "&:hover": {
        borderColor: "#94a3b8",
      },
    }),
    valueContainer: (base: any) => ({
      ...base,
      padding: "0 12px",
    }),
    placeholder: (base: any) => ({
      ...base,
      color: "#94a3b8",
    }),
    menu: (base: any) => ({
      ...base,
      borderRadius: "10px",
      overflow: "hidden",
      zIndex: 20,
    }),
    singleValue: (base: any) => ({
      ...base,
      color: "#0f172a",
    }),
  };

  return (
    <div className="container-fluid py-4 px-4">
      <div
        style={{
          background: "#ffffff",
          borderRadius: "18px",
          padding: "24px 28px",
          boxShadow: "0 12px 30px rgba(15, 23, 42, 0.08)",
          border: "1px solid #eef2f7",
          marginBottom: "24px",
        }}
      >
        <div className="d-flex align-items-center gap-3">
          <div
            style={{
              width: "56px",
              height: "56px",
              borderRadius: "14px",
              background: "linear-gradient(135deg, #fff1f2 0%, #ffe4e6 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 8px 18px rgba(239, 68, 68, 0.14)",
            }}
          >
            <i
              className="las la-bell"
              style={{ fontSize: "28px", color: "#dc2626" }}
            ></i>
          </div>

          <div>
            <h2
              style={{
                margin: 0,
                fontSize: "30px",
                fontWeight: 700,
                color: "#0f172a",
                lineHeight: 1.2,
              }}
            >
              {translate("Notification Center")}
            </h2>
            <p
              style={{
                margin: "6px 0 0 0",
                color: "#64748b",
                fontSize: "14px",
              }}
            >
              {translate("Send push notifications to platform users")}
            </p>
          </div>
        </div>
      </div>

      <div
        style={{
          maxWidth: "980px",
          background: "#ffffff",
          borderRadius: "18px",
          padding: "30px",
          boxShadow: "0 12px 30px rgba(15, 23, 42, 0.06)",
          border: "1px solid #eef2f7",
        }}
      >
        <h5
          style={{
            fontSize: "24px",
            fontWeight: 700,
            color: "#0f172a",
            marginBottom: "24px",
          }}
        >
          {translate("Send a notification to user")}
        </h5>

        <Form onSubmit={handleSubmit}>
          <Form.Group controlId="user">
            <Form.Label style={labelStyle}>{translate("User")}</Form.Label>
            <Select
              options={usersOptions}
              styles={selectStyles}
              value={selectedUser}
              onChange={(option) =>
                setSelectedUser(option as SelectOption | null)
              }
              placeholder={translate("Select a user")}
              isClearable
            />
          </Form.Group>

          {selectedUser && selectedUser.value !== "" && (
            <div className="mt-3">
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "8px",
                  padding: "6px 14px",
                  borderRadius: "999px",
                  background:
                    "linear-gradient(135deg, #ecfdf3 0%, #d1fae5 100%)",
                  color: "#166534",
                  border: "1px solid #86efac",
                  boxShadow: "0 4px 14px rgba(34, 197, 94, 0.16)",
                  fontSize: "12px",
                  fontWeight: 600,
                }}
              >
                <span
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    background: "#22c55e",
                  }}
                />
                {translate("Selected")}: {selectedUser.label}
              </span>
            </div>
          )}

          <Form.Group controlId="title" className="mt-4">
            <Form.Label style={labelStyle}>{translate("Title")}</Form.Label>
            <Form.Control
              type="text"
              placeholder={translate("Enter title")}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
              style={fieldStyle}
            />
          </Form.Group>

          <Form.Group controlId="body" className="mt-4">
            <Form.Label style={labelStyle}>{translate("Message")}</Form.Label>
            <Form.Control
              as="textarea"
              placeholder={translate("Enter the message")}
              value={body}
              rows={5}
              onChange={(e) => setBody(e.target.value)}
              required
              style={textareaStyle}
            />
          </Form.Group>

          <div className="d-flex justify-content-start gap-2 mt-4">
            <Button
              type="button"
              onClick={sendNotification}
              disabled={buttonClicked}
              style={primaryButtonBaseStyle}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "#fde047";
                e.currentTarget.style.boxShadow = "none";
                e.currentTarget.style.outline = "none";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "#facc15";
                e.currentTarget.style.transform = "scale(1)";
                e.currentTarget.style.boxShadow = "none";
                e.currentTarget.style.outline = "none";
              }}
              onMouseDown={(e) => {
                e.currentTarget.style.transform = "scale(0.97)";
                e.currentTarget.style.background = "#facc15";
                e.currentTarget.style.boxShadow = "none";
                e.currentTarget.style.outline = "none";
              }}
              onMouseUp={(e) => {
                e.currentTarget.style.transform = "scale(1)";
                e.currentTarget.style.background = "#fde047";
                e.currentTarget.style.boxShadow = "none";
                e.currentTarget.style.outline = "none";
              }}
              onFocus={(e) => {
                e.currentTarget.style.boxShadow = "none";
                e.currentTarget.style.outline = "none";
                e.currentTarget.style.background = "#facc15";
              }}
            >
              <i className="las la-paper-plane"></i>
              {translate("Send notification")}
            </Button>

            <Button
              type="button"
              onClick={() => {
                setSelectedUser(null);
                setTitle("");
                setBody("");
              }}
              style={secondaryButtonBaseStyle}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "#e2e8f0";
                e.currentTarget.style.boxShadow = "none";
                e.currentTarget.style.outline = "none";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "#f1f5f9";
                e.currentTarget.style.boxShadow = "none";
                e.currentTarget.style.outline = "none";
              }}
              onFocus={(e) => {
                e.currentTarget.style.boxShadow = "none";
                e.currentTarget.style.outline = "none";
              }}
            >
              {translate("Cancel")}
            </Button>
          </div>
        </Form>
      </div>
    </div>
  );
};

export default NotificationCenter;
